import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "axios";

const academicYearFromDate = (dateStr) => {
  if (!dateStr) return "";
  const [yearStr, monthStr] = String(dateStr).split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!year || !month) return "";
  return month >= 7 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

const GetExpenses = () => {
  const [searchText, setSearchText] = React.useState("");
  const { t, i18n } = useTranslation();
  const [dataSource, setDataSource] = React.useState([]);
  const [deleteItemId, setDeleteItemId] = React.useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [showAllYears, setShowAllYears] = React.useState(false);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });
  const token = localStorage.getItem("token");

  React.useEffect(() => {
    getApiProducts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  React.useEffect(() => {
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, [searchText, showAllYears]);

  const getApiProducts = async (allYears = false) => {
    setLoading(true);
    try {
      const response = await axios.get(
        allYears
          ? "https://api.clever-school.com/api/reports/expenses-report"
          : "https://api.clever-school.com/api/expenses",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const items = allYears
        ? response.data.expenses || []
        : response.data;

      const rows = [...items]
        .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
        .map((item, index) => ({
          index: index + 1,
          id: item.id,
          bill_id: item.bill_id,
          description: item.description,
          amount: item.amount,
          date: item.date,
          academic_year: academicYearFromDate(item.date),
        }));

      setDataSource(rows);
      return true;
    } catch (error) {
      console.error("Error fetching data:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleToggleYears = async () => {
    const next = !showAllYears;
    setShowAllYears(next);
    const ok = await getApiProducts(next);
    if (!ok) {
      setShowAllYears(!next);
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmedDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `https://api.clever-school.com/api/expenses/${deleteItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await getApiProducts(showAllYears);
      setDeleteItemId(null);
      setIsDeleteModalVisible(false);
    } catch (error) {
      console.error("Error deleting product:", error);
    } finally {
      setLoading(false);
    }
  };

  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  const columns = [
    { field: "index", headerName: "#", width: 60 },
    { field: "bill_id", headerName: t("billId"), width: 120 },
    { field: "description", headerName: t("description"), flex: 1, minWidth: 180 },
    { field: "amount", headerName: t("amount"), width: 120 },
    { field: "date", headerName: t("date"), width: 130 },
    ...(showAllYears
      ? [
          {
            field: "academic_year",
            headerName: t("academicYear"),
            width: 150,
          },
        ]
      : []),
    {
      field: "action",
      headerName: t("action"),
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Link
            to={`/GetExpenses/UpdateExpenses/${params.row.id}`}
            style={{ textDecoration: "none" }}
          >
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: "#3f51b5",
                color: "#fff",
                "&:hover": { backgroundColor: "#303f9f" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>
          <Link>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDelete(params.row.id)}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": { backgroundColor: "#b71c1c" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>
        </div>
      ),
    },
  ];

  const filteredRows = dataSource.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div
      style={{
        margin: "25px",
        height: "calc(100vh - 50px)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          direction,
          textAlign,
          marginBottom: "16px",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <TextField
          placeholder={t("searchHere")}
          variant="outlined"
          sx={{
            flex: "1",
            minWidth: "250px",
            maxWidth: "400px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#ddd",
              },
              "&:hover fieldset": {
                borderColor: "#999",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#3f51b5",
              },
            },
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant={showAllYears ? "contained" : "outlined"}
            startIcon={<HistoryIcon />}
            onClick={handleToggleYears}
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              padding: "10px 20px",
              whiteSpace: "nowrap",
              ...(showAllYears
                ? {
                    backgroundColor: "#3f51b5",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#303f9f" },
                  }
                : {
                    borderColor: "#3f51b5",
                    color: "#3f51b5",
                    "&:hover": {
                      borderColor: "#303f9f",
                      backgroundColor: "#f5f7ff",
                    },
                  }),
            }}
          >
            {showAllYears
              ? t("showCurrentAcademicYear")
              : t("showAllAcademicYears")}
          </Button>
          <Link to="/AddExpenses" style={{ textDecoration: "none" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#3f51b5",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#303f9f",
              },
              borderRadius: "6px",
              padding: "10px 20px",
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            {t("AddExpenses")}
          </Button>
        </Link>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
          <Typography variant="h6" sx={{ marginTop: 2 }}>
            {t("Loading Expenses...")}
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            marginTop: 2,
            padding: 2,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <DataGrid
            rows={filteredRows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              flex: 1,
              height: "100%",
              width: "100%",
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#3f51b5",
                color: "black",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "16px",
                  fontWeight: "bold",
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e0e0e0",
              },
            }}
          />
        </Paper>
      )}

      <Dialog
        open={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
      >
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogContent>
          <p>{t("deleteConfirmation1")}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteModalVisible(false)}>
            {t("cancel")}
          </Button>
          <Button onClick={handleConfirmedDelete} color="error">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GetExpenses;
